<template>
  <div class="wh-screen">
    <global-confirm-box ref="confirmBox" />
    <warehouse-purchase-receipt-dialog v-model="dialogStatus" :edited-item="editedItem" @success="onSuccess" />
    <warehouse-purchase-order-dialog v-model="purchaseOrderDialog" :edited-item="editedItem" @success="onSuccess" />
    <!-- Slanje dobavljaču i njegov odgovor: na računaru desni panel ljuske, na telefonu donji list -->
    <ui-sheet v-model="flowSheet" :title="flowEntry ? flowEntry.title : ''" :ready="Boolean(flowEntry)">
      <template #default>
        <component :is="flowEntry.component" v-bind="flowEntry.props" :key="flowEntry.key" />
      </template>
    </ui-sheet>
    <warehouse-doc-screen
      v-bind="docProps"
      :mobile="device.isMobile"
      :chips="flowChips"
      :chip="awaitingOnly ? 'awaiting' : 'all'"
      @update:chip="awaitingOnly = $event === 'awaiting'"
      @update:aggregate="aggregateMode = $event"
      @hint="setFiltersToLastEntry"
      @action="onDocAction"
      @open-row="openRowDoc"
      @toast="$emit('toast', $event)"
    >
      <template #cell="{ row, column }">
        <template v-if="column.key === 'status'">
          <v-menu v-if="!device.isMobile" offset-y left content-class="rd-vmenu">
            <template #activator="{ on, attrs }">
              <button
                type="button"
                class="wh-status wh-status--button"
                :class="`wh-status--${statusTone(row.status)}`"
                :aria-label="`${$t('status')}: ${row.statusLabel}`"
                data-status-menu
                v-bind="attrs"
                @click.stop
                v-on="on"
              >
                {{ row.statusLabel }}
                <v-icon size="14">mdi-chevron-down</v-icon>
              </button>
            </template>
            <ul class="rd-vmenu__list" role="menu">
              <li v-for="item in statuses" :key="item.value">
                <button type="button" role="menuitem" :data-status-value="item.value" :disabled="item.value === row.status" @click="changeStatus(row, item.value)">
                  <i class="wh-status-dot" :class="`wh-status-dot--${statusTone(item.value)}`"></i>
                  {{ item.name }}
                </button>
              </li>
            </ul>
          </v-menu>
          <span v-else class="wh-status" :class="`wh-status--${statusTone(row.status)}`">{{ row.statusLabel }}</span>
        </template>
        <template v-else-if="column.key === 'flow'">
          <button
            v-if="flowView(row.raw)"
            type="button"
            class="wh-flow"
            data-flow
            :aria-label="`${$t('supplier_po.flow')}: ${flowView(row.raw).label}`"
            @click.stop="openResponse(row.raw)"
          >
            <ui-chip :tone="flowView(row.raw).tone" :icon="flowView(row.raw).icon" pill>
              {{ flowView(row.raw).label }}<template v-if="flowView(row.raw).changes"> · {{ $t("supplier_po.flow_changes", { n: flowView(row.raw).changes }) }}</template>
            </ui-chip>
          </button>
          <button
            v-else-if="canSend(row.raw)"
            type="button"
            class="ui-btn ui-btn--sm ui-btn--accent"
            data-action="send-inline"
            @click.stop="openSend(row.raw)"
          >
            <v-icon size="14" color="currentColor">mdi-send</v-icon>
            {{ $t("supplier_po.send_short") }}
          </button>
          <span v-else class="rd-muted">—</span>
        </template>
        <template v-else>{{ cellText(row[column.key]) }}</template>
      </template>
      <template #row-actions="{ row }">
        <warehouse-row-button
          v-if="canSend(row.raw)"
          icon="mdi-send"
          :label="$t('supplier_po.send')"
          tone="pri"
          action="send"
          @click="openSend(row.raw)"
        />
        <warehouse-row-button
          v-if="row.raw.supplier_flow && row.raw.supplier_flow.dispatched"
          icon="mdi-message-reply-text-outline"
          :label="$t('supplier_po.view_response')"
          :tone="row.raw.supplier_flow.awaiting_decision ? 'pri' : 'plain'"
          action="response"
          @click="openResponse(row.raw)"
        />
        <warehouse-row-button
          icon="mdi-file-document-arrow-right-outline"
          :label="$t('warehouse.supplier_delivered')"
          action="delivered"
          @click="openDelivered(row)"
        />
        <warehouse-row-button icon="mdi-eye" :label="$t('overview')" tone="pri" action="view" @click="openRowDoc(row)" />
        <warehouse-row-button icon="mdi-trash-can-outline" :label="$t('delete')" tone="danger" action="delete" @click="deleteRowDoc(row)" />
      </template>
    </warehouse-doc-screen>
  </div>
</template>

<script>
import WarehouseTabPurchase from "@/components/warehouse/tabs/WarehouseTabPurchase.vue";
import WarehouseDocScreen from "../../docs/WarehouseDocScreen.vue";
import WarehouseRowButton from "../../docs/WarehouseRowButton.vue";
import WarehouseSupplierSendPanel from "../../panels/WarehouseSupplierSendPanel.vue";
import WarehouseSupplierResponsePanel from "../../panels/WarehouseSupplierResponsePanel.vue";
import hasDocumentListScreen from "../../docs/hasDocumentListScreen";
import { PURCHASE_STATUS_TONE } from "../../docs/docStatus";

/**
 * Naručeno (zahtevi za ponudu i narudžbenice): lista dokumenata sa statusom koji se menja iz oznake, zbirni prikaz po
 * artiklu, "Naruči" u traci alata. Nasleđuje učitavanje, filtriranje, promenu statusa, brisanje, štampu, Excel i dijaloge.
 */
export default {
  name: "WarehouseScreenPurchase",
  extends: WarehouseTabPurchase,
  mixins: [hasDocumentListScreen],
  components: { WarehouseDocScreen, WarehouseRowButton },
  data: () => ({ flowSheet: false, flowEntry: null }),
  computed: {
    docConfig() {
      return {
        icon: "mdi-cart-arrow-down",
        hintKey: "warehouse.last_purchase",
        emptyTitleKey: "no_purchases",
        emptyTextKey: "no_purchases_yet",
        open: "editItem",
        remove: "deleteItem",
      };
    },
    docExpandable() {
      return false;
    },
    docColumns() {
      return [
        { key: "id", label: "ID", mobile: "sub", muted: true, num: true, prefix: "#" },
        { key: "number", label: this.$t("warehouse.document_number"), main: true, mobile: "title" },
        { key: "document_type", label: this.$t("warehouse.document_type"), mobile: "meta" },
        { key: "status", label: this.$t("status"), mobile: "badge" },
        { key: "flow", label: this.$t("supplier_po.flow"), mobile: "meta" },
        { key: "document_date", label: this.$t("date"), mobile: "meta" },
        { key: "supplierName", label: this.$t("supplier"), mobile: "meta" },
        { key: "offer_deadline", label: this.$t("warehouse.offers_deadline"), mobile: "meta" },
        { key: "delivery_deadline", label: this.$t("warehouse.delivery_deadline"), mobile: "meta" },
      ];
    },
    docActions() {
      return [
        {
          key: "order",
          label: this.$t("order"),
          icon: "mdi-cart-outline",
          tone: "ok",
          menu: [
            { key: "request", label: this.$t("warehouse.request_quote"), icon: "mdi-file-document-outline" },
            { key: "purchase", label: this.$t("warehouse.purchase_order"), icon: "mdi-cart-arrow-down" },
          ],
        },
      ];
    },
    docKpiExtra() {
      return [];
    },
    /** Filter "Čeka moju odluku": samo kad ima narudžbenica na koje je dobavljač odgovorio. */
    flowChips() {
      if (!this.awaitingCount) {
        return [];
      }
      return [
        { value: "all", label: this.$t("all") },
        { value: "awaiting", label: this.$t("supplier_po.awaiting_filter"), count: this.awaitingCount, dot: "#d97706" },
      ];
    },
  },
  methods: {
    /** Oznaka stanja slanja dobavljaču u listi (Poslato, Otvoreno, Odgovoreno, Čeka odluku); null ako nije slato. */
    flowView(doc) {
      const flow = doc.supplier_flow;
      if (!flow || !flow.dispatched) {
        return null;
      }
      const date = (value) => (value ? this.localeDate(value) : "");
      let state = "sent";
      if (flow.awaiting_decision) state = "awaiting";
      else if (flow.responded_at) state = "responded";
      else if (!flow.has_active_link) state = "inactive";
      else if (flow.opened_at) state = "opened";
      const view = {
        awaiting: { tone: "warn", icon: "mdi-message-reply-text-outline", label: this.$t("supplier_po.flow_awaiting") },
        responded: { tone: "ok", icon: "mdi-check", label: this.$t("supplier_po.flow_responded", { date: date(flow.responded_at) }) },
        opened: { tone: "blue", icon: "mdi-eye-outline", label: this.$t("supplier_po.flow_opened", { date: date(flow.opened_at) }) },
        inactive: { tone: "bad", icon: "mdi-link-off", label: this.$t("supplier_po.flow_inactive") },
        sent: { tone: "plain", icon: "mdi-send", label: this.$t("supplier_po.flow_sent", { date: date(flow.last_sent_at) }) },
      }[state];
      return { ...view, changes: flow.changes_count || 0 };
    },
    docRow(item) {
      const label = this.translate(this.statuses.find((status) => status.value === item.status)?.name) || "";
      return {
        key: item.id,
        raw: item,
        group: { id: item.warehouse_id, name: this.toLatin(item.warehouse?.name || "") || this.$t("warehouse.unknown") },
        id: item.id,
        number: item.number || "",
        document_type: this.documentTypes[item.document_type] || "",
        status: item.status,
        statusLabel: label,
        document_date: item.document_date ? this.localeDate(item.document_date) : "",
        supplierName: item.supplierName || "",
        flow: this.flowText(item),
        offer_deadline: item.offer_deadline ? this.localeDate(item.offer_deadline) : "",
        delivery_deadline: item.delivery_deadline ? this.localeDate(item.delivery_deadline) : "",
        sort: {
          id: Number(item.id) || 0,
          document_date: item.document_date || "",
          status: label,
          flow: this.flowText(item),
          offer_deadline: item.offer_deadline || "",
          delivery_deadline: item.delivery_deadline || "",
        },
      };
    },
    statusTone(status) {
      return PURCHASE_STATUS_TONE[status] || "neutral";
    },
    /** Slanje dobavljaču i njegov odgovor (panel / donji list umjesto starih dijaloga). */
    openSend(item) {
      this.openFlow("send", item, this.$t("supplier_po.send_title"), WarehouseSupplierSendPanel);
    },
    openResponse(item) {
      this.openFlow("response", item, this.$t("supplier_po.response_title"), WarehouseSupplierResponsePanel);
    },
    openFlow(kind, item, title, component) {
      this.flowDocument = item;
      const subtitle = [`#${item.number || item.id}`, item.supplierName].filter(Boolean).join(" · ");
      const entry = {
        key: `po-${kind}-${item.id}`,
        title,
        subtitle,
        component,
        root: true,
        props: { document: item, onDone: this.onSuccess },
      };
      if (kind === "response" && this.canSend(item)) {
        entry.props.onResend = () => this.openSend(item);
      }
      if (this.device.isMobile) {
        entry.props.onClose = () => (this.flowSheet = false);
        this.flowEntry = Object.freeze(entry);
        this.flowSheet = true;
      } else {
        this.$emit("open-panel", entry);
      }
    },
    /** Izbor "Naruči": zahtev za ponudu ili narudžbenica. */
    onDocAction(key) {
      this.orderGroceries(key === "purchase");
    },
    openDelivered(row) {
      this.editedItem = row.raw;
      this.dialogStatus = true;
    },
    /** Novi status se odmah vidi u listi, a stari tab ga šalje serveru (pri grešci ponovo učitava). */
    async changeStatus(row, value) {
      if (value === row.raw.status) {
        return;
      }
      const document = this.documents.find((item) => item.id === row.raw.id);
      if (document) {
        this.$set(document, "status", value);
      }
      await this.updateStatus({ id: row.raw.id, status: value });
    },
  },
};
</script>
